import React, { useId } from 'react';
import { HeadSilhouette } from './AvatarHead';

export default function AvatarHair({
  characterType = 'human_male',
  hair,
  headShape = 'circle',
  centerX = 200,
  hairY = 102,
  layer = 'front',
}) {

  // Each avatar needs its own gradient ID because previews
  // and option thumbnails can appear together.

  const instanceId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const fadeId = `avatar-fade-${instanceId}`;
  const scalpClipId = `avatar-scalp-${instanceId}`;

  // Head geometry uses full-avatar coordinates.
  // Hair artwork uses coordinates relative to centerX and hairY.
  const scalpClip = (
    <defs>
      <clipPath
        id={scalpClipId}
        clipPathUnits="userSpaceOnUse"
      >
        <HeadSilhouette
          characterType={characterType}
          shape={headShape}
          transform={`translate(${-centerX} ${-hairY})`}
        />
      </clipPath>
    </defs>
  );

  if (!hair || hair.id === 'bald') {
    return null;
  }

  const isFemale = characterType === 'human_female';
  const isSquare = headShape === 'square';

  const color = hair.color || '#292332';
  const highlight = hair.highlight || '#42394E';

  const isNewStyle = [
    'waves_01',
    'braids_01',
    'side_braid_01',
    'bun_01',
    'bob_01',
  ].includes(hair.id);

  // Back hair is drawn before the head and clothing.
  if (layer === 'back') {
    if (hair.id === 'waves_01') {
      return (
        <g transform={`translate(${centerX} ${hairY})`}>
          <path
            d="
              M-79 25
              Q-109 55 -101 97
              Q-118 127 -102 153
              Q-117 179 -96 205
              Q-69 225 -48 204
              L48 204
              Q69 225 96 205
              Q117 179 102 153
              Q118 127 101 97
              Q109 55 79 25
              Z
            "
            fill={color}
          />

          <g
            fill="none"
            stroke={highlight}
            strokeWidth="7"
            strokeLinecap="round"
          >
            <path d="M-86 72 Q-98 96 -86 119 Q-99 145 -87 167" />
            <path d="M86 72 Q98 96 86 119 Q99 145 87 167" />
          </g>
        </g>
      );
    }

    if (hair.id === 'bob_01') {
      return (
        <g transform={`translate(${centerX} ${hairY})`}>
          <path
            d="
              M-78 23
              Q-104 53 -98 105
              L-101 160
              Q-99 181 -68 183
              L68 183
              Q99 181 101 160
              L98 105
              Q104 53 78 23
              Z
            "
            fill={color}
          />

          <path
            d="M-87 83 L-86 149 M87 83 L86 149"
            fill="none"
            stroke={highlight}
            strokeWidth="6"
            strokeLinecap="round"
          />
        </g>
      );
    }

    // Other hairstyles have no back layer.
    return null;
  }

  if (isNewStyle) {
    // These styles share a center-parted cap.
    // The forehead stays clear of brows and expressions.
    const cap = isSquare
      ? `
        M-82 67
        L-85 43
        Q-86 -6 -36 -9
        Q0 -15 36 -9
        Q86 -6 85 43
        L82 67
        Q68 55 65 29
        Q27 31 0 13
        Q-27 31 -65 29
        Q-68 55 -82 67
        Z
      `
      : `
        M-84 67
        Q-96 28 -61 3
        Q-32 -18 0 -11
        Q32 -18 61 3
        Q96 28 84 67
        Q68 55 65 29
        Q27 31 0 13
        Q-27 31 -65 29
        Q-68 55 -84 67
        Z
      `;

    function braid(x, y, angle = 0) {
      return (
        <g transform={`translate(${x} ${y}) rotate(${angle})`}>
          <path
            d="
              M-12 -5
              Q-22 5 -13 18
              Q-23 29 -12 42
              Q-21 54 -10 66
              Q-16 76 0 88
              Q16 76 10 66
              Q21 54 12 42
              Q23 29 13 18
              Q22 5 12 -5
              Z
            "
            fill={color}
          />

          <path
            d="
              M-9 9 L8 21
              M-8 32 L8 44
              M-7 55 L6 66
            "
            fill="none"
            stroke={highlight}
            strokeWidth="5"
            strokeLinecap="round"
          />

          <rect
            x="-8"
            y="76"
            width="16"
            height="7"
            rx="3.5"
            fill="#007AFF"
          />

          <path
            d="M-5 84 L-9 98 Q0 103 9 98 L5 84 Z"
            fill={color}
          />
        </g>
      );
    }

    return (
      <g transform={`translate(${centerX} ${hairY})`}>
        {/* Bun sits above the cap, with a visible connecting base. */}
        {hair.id === 'bun_01' && (
          <>
            <ellipse
              cx="0"
              cy="-22"
              rx="34"
              ry="30"
              fill={color}
            />

            <path
              d="M-20 -29 Q-2 -46 19 -29"
              fill="none"
              stroke={highlight}
              strokeWidth="7"
              strokeLinecap="round"
            />

            <path
              d="M-21 -6 Q0 -14 21 -6"
              fill="none"
              stroke={highlight}
              strokeWidth="5"
              strokeLinecap="round"
            />
          </>
        )}

        <path d={cap} fill={color} />

        <g
          fill="none"
          stroke={highlight}
          strokeWidth="6"
          strokeLinecap="round"
        >
          <path d="M-54 15 Q-34 1 -14 2" />
          <path d="M14 2 Q34 1 54 15" />
        </g>

        {hair.id === 'waves_01' && (
          <g
            fill="none"
            stroke={color}
            strokeWidth="18"
            strokeLinecap="round"
          >
            <path
              d="
                M-76 52
                Q-91 83 -81 112
                Q-94 138 -81 162
                Q-88 182 -73 193
              "
            />

            <path
              d="
                M76 52
                Q91 83 81 112
                Q94 138 81 162
                Q88 182 73 193
              "
            />
          </g>
        )}

        {hair.id === 'bob_01' && (
          <g fill={color}>
            <path
              d="
                M-79 46
                Q-93 72 -87 116
                L-83 166
                Q-73 174 -64 165
                L-68 103
                Q-72 72 -65 29
                Z
              "
            />

            <path
              d="
                M79 46
                Q93 72 87 116
                L83 166
                Q73 174 64 165
                L68 103
                Q72 72 65 29
                Z
              "
            />
          </g>
        )}

        {hair.id === 'braids_01' && (
          <>
            <path
              d="M-77 52 Q-86 88 -82 122 M77 52 Q86 88 82 122"
              fill="none"
              stroke={color}
              strokeWidth="24"
              strokeLinecap="round"
            />

            {braid(-82, 115, 5)}
            {braid(82, 115, -5)}
          </>
        )}

        {hair.id === 'side_braid_01' && (
          <>
            <path
              d="M78 49 Q93 83 78 122"
              fill="none"
              stroke={color}
              strokeWidth="27"
              strokeLinecap="round"
            />

            {braid(78, 115, 8)}
          </>
        )}
      </g>
    );
  }

  const maleOutline = isSquare
    ? `
      M-82 65
      L-82 48
      Q-82 0 -34 0
      L34 0
      Q82 0 82 48
      L82 65
      Q68 57 66 37
      Q40 43 16 25
      Q-16 47 -65 35
      Q-66 56 -82 65
      Z
    `
    : `
      M-84 66
      C-89 27 -57 -4 -15 -5
      C24 -13 72 7 83 43
      Q87 54 84 66
      Q70 58 66 36
      Q40 44 16 24
      Q-17 47 -65 35
      Q-68 57 -84 66
      Z
    `;

  const femaleOutline = isSquare
    ? `
      M-82 70
      L-86 45
      Q-90 -5 -35 -10
      Q16 -20 56 3
      Q88 20 85 57
      L80 75
      Q65 60 63 33
      Q44 31 34 19
      Q14 47 -25 49
      Q-48 51 -64 43
      Q-66 61 -82 70
      Z
    `
    : `
      M-84 70
      Q-96 38 -73 10
      Q-50 -18 -8 -13
      Q30 -20 61 6
      Q91 28 85 58
      L80 75
      Q66 61 63 33
      Q44 31 34 19
      Q14 47 -25 49
      Q-48 51 -64 43
      Q-67 62 -84 70
      Z
    `;

  let artwork;

  switch (hair.id) {
    case 'short_01':
      artwork = (
        <>
          <path
            d={isFemale ? femaleOutline : maleOutline}
            fill={color}
          />

          <path
            d={
              isFemale
                ? 'M-53 17 Q-17 -3 17 10'
                : 'M-48 17 Q-19 4 8 13'
            }
            fill="none"
            stroke={highlight}
            strokeWidth="9"
            strokeLinecap="round"
          />
        </>
      );
      break;

    case 'fade_01':
      artwork = (
        <>
          <defs>
            <linearGradient
              id={fadeId}
              x1="0"
              y1="15"
              x2="0"
              y2="78"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={color} />
              <stop
                offset="55%"
                stopColor={color}
                stopOpacity="0.65"
              />
              <stop
                offset="100%"
                stopColor={color}
                stopOpacity="0"
              />
            </linearGradient>
          </defs>

          {/* Close-cut sides gradually reveal the skin. */}
          <path
            d={
              isSquare
                ? `
                  M-82 78 L-82 46
                  Q-82 0 -34 0
                  L34 0 Q82 0 82 46
                  L82 78 L68 74 L62 26
                  Q0 10 -62 26
                  L-68 74 Z
                `
                : `
                  M-83 78
                  Q-91 24 -49 3
                  Q0 -18 49 3
                  Q91 24 83 78
                  L69 74 L62 26
                  Q0 10 -62 26
                  L-69 74 Z
                `
            }
            fill={`url(#${fadeId})`}
          />

          {/* Compact top with a clean hairline. */}
          <path
            d={
              isSquare
                ? `
                  M-67 31 L-66 13
                  Q-64 -8 -35 -8
                  L35 -8 Q64 -8 66 13
                  L67 31
                  Q35 24 0 27
                  Q-35 24 -67 31 Z
                `
                : `
                  M-68 31
                  Q-70 4 -43 -5
                  Q0 -19 43 -5
                  Q70 4 68 31
                  Q35 24 0 27
                  Q-35 24 -68 31 Z
                `
            }
            fill={color}
          />

          <path
            d="M-43 10 Q-12 -1 20 6"
            fill="none"
            stroke={highlight}
            strokeWidth="7"
            strokeLinecap="round"
          />
        </>
      );
      break;

    case 'mohawk_01':
      artwork = (
        <>
          <path
            d="
              M-28 39
              Q-35 15 -29 -10
              L-21 -4
              L-16 -37
              L-5 -27
              L5 -52
              L17 -29
              L29 -36
              L29 -9
              L38 -13
              Q37 15 27 39
              Q0 29 -28 39
              Z
            "
            fill={color}
          />

          <path
            d="M-9 15 Q-8 -6 3 -23"
            fill="none"
            stroke={highlight}
            strokeWidth="7"
            strokeLinecap="round"
          />
        </>
      );
      break;


    case 'balding_01':
      artwork = (
        <g clipPath={`url(#${scalpClipId})`}>

          {/* Open crown with two separate side sections. */}
          <path
            d={
              isSquare
                ? `
                  M-82 82 L-85 48
                  Q-84 18 -61 10
                  Q-57 25 -65 43
                  L-68 77
                  Q-74 86 -82 82 Z
                `
                : `
                  M-82 82
                  Q-93 48 -77 24
                  Q-70 14 -60 11
                  Q-58 29 -66 45
                  L-69 77
                  Q-75 86 -82 82 Z
                `
            }
            fill={color}
          />

          <path
            d={
              isSquare
                ? `
                  M82 82 L85 48
                  Q84 18 61 10
                  Q57 25 65 43
                  L68 77
                  Q74 86 82 82 Z
                `
                : `
                  M82 82
                  Q93 48 77 24
                  Q70 14 60 11
                  Q58 29 66 45
                  L69 77
                  Q75 86 82 82 Z
                `
            }
            fill={color}
          />



          <path
            d="M-76 43 L-75 62 M76 43 L75 62"
            fill="none"
            stroke={highlight}
            strokeWidth="5"
            strokeLinecap="round"
          />
        </g>
      );
      break;

    case 'curl_01':
      artwork = (
        <>
          <path
            d="
              M-17 20
              C-38 6 -34 -21 -13 -30
              C8 -40 34 -27 31 -7
              C29 9 7 14 0 2
              C-5 -7 3 -17 12 -13
            "
            fill="none"
            stroke={color}
            strokeWidth="18"
            strokeLinecap="round"
          />

          <path
            d="M-23 -4 Q-27 -20 -10 -25"
            fill="none"
            stroke={highlight}
            strokeWidth="5"
            strokeLinecap="round"
          />
        </>
      );
      break;

    case 'puffs_01':
      artwork = (
        <>
          {/* Rounded lobes make each puff feel soft. */}
          <g fill={color}>
            <circle cx="-77" cy="3" r="30" />
            <circle cx="-94" cy="-13" r="23" />
            <circle cx="-73" cy="-25" r="26" />
            <circle cx="-53" cy="-9" r="23" />

            <circle cx="77" cy="3" r="30" />
            <circle cx="94" cy="-13" r="23" />
            <circle cx="73" cy="-25" r="26" />
            <circle cx="53" cy="-9" r="23" />
          </g>

          {/* Pulled-back hair follows the selected head shape. */}
          <path
            d={
              isSquare
                ? `
                  M-82 58 L-82 43
                  Q-82 -3 -34 -3
                  L34 -3 Q82 -3 82 43
                  L82 58
                  Q70 45 66 27
                  Q35 24 0 12
                  Q-35 24 -66 27
                  Q-70 45 -82 58 Z
                `
                : `
                  M-83 58
                  Q-91 15 -49 -2
                  Q0 -22 49 -2
                  Q91 15 83 58
                  Q70 45 66 27
                  Q35 24 0 12
                  Q-35 24 -66 27
                  Q-70 45 -83 58 Z
                `
            }
            fill={color}
          />

          <g
            fill="none"
            stroke={highlight}
            strokeWidth="7"
            strokeLinecap="round"
          >
            <path d="M-94 -16 Q-87 -31 -72 -30" />
            <path d="M64 -29 Q79 -34 90 -22" />
            <path d="M-43 12 Q-23 3 -9 3" />
            <path d="M14 4 Q34 6 47 14" />
          </g>
        </>
      );
      break;

    default:
      return null;
  }


  return (
    <g transform={`translate(${centerX} ${hairY})`}>
      {scalpClip}
      {artwork}
    </g>
  );

}
